import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'

import { PatientList } from '../../../providers/Patients/types'
import { Provider } from '../../../providers/ProvidersProvider/types'
import {
  CreateProviderTaskRequest,
  ProviderTaskBarrier,
  ProviderTaskPriorityLevel,
  ProviderTaskResponse,
  ProviderTaskStatus,
  ProviderTaskType,
  ProviderType,
} from '../../../providers/ProviderTask/ProviderTask.provider'

interface CreateProviderTaskModalProps {
  loading: boolean
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  showCreateModal: boolean
  setShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>
  allActivePatients: PatientList
  taskTypes: ProviderTaskType[]
  formProviderTypeId: number | null
  setFormProviderTypeId: React.Dispatch<React.SetStateAction<number | null>>
  providerTypes: ProviderType[]
  providers: Provider[]
  priorityLevels: ProviderTaskPriorityLevel[]
  statuses: ProviderTaskStatus[]
  barriers: ProviderTaskBarrier[]
  formTaskTypeId: number | ''
  setFormTaskTypeId: React.Dispatch<React.SetStateAction<number>>
  formAssignedToProviderId: number | null
  setFormAssignedToProviderId: React.Dispatch<
    React.SetStateAction<number | null>
  >
  formPriorityLevelId: number | ''
  setFormPriorityLevelId: React.Dispatch<React.SetStateAction<number>>
  formStatusId: number | ''
  setFormStatusId: React.Dispatch<React.SetStateAction<number>>
  formBarrierId: number | null
  setFormBarrierId: React.Dispatch<React.SetStateAction<number | null>>
  formDueAt: Dayjs | null
  setFormDueAt: React.Dispatch<React.SetStateAction<Dayjs | null>>
  formAssignedAt: Dayjs | null
  formPatientId: number
  setFormPatientId: React.Dispatch<React.SetStateAction<number>>
  onCreateTask: (request: CreateProviderTaskRequest) => Promise<any>
  setOptimisticTasks: React.Dispatch<
    React.SetStateAction<ProviderTaskResponse[]>
  >
}

export const CreateProviderTaskModal = ({
  loading,
  setLoading,
  showCreateModal,
  setShowCreateModal,
  allActivePatients,
  taskTypes,
  formProviderTypeId,
  setFormProviderTypeId,
  providerTypes,
  providers,
  priorityLevels,
  statuses,
  barriers,
  formTaskTypeId,
  setFormTaskTypeId,
  formAssignedToProviderId,
  setFormAssignedToProviderId,
  formPriorityLevelId,
  setFormPriorityLevelId,
  formStatusId,
  setFormStatusId,
  formBarrierId,
  setFormBarrierId,
  formDueAt,
  setFormDueAt,
  formAssignedAt,
  formPatientId,
  setFormPatientId,
  onCreateTask,
  setOptimisticTasks,
}: CreateProviderTaskModalProps) => {
  const handleCreateTask = async () => {
    setLoading(true)

    const requiredFields = [
      { value: formPatientId, label: 'patient' },
      { value: formTaskTypeId, label: 'task type' },
      { value: formPriorityLevelId, label: 'priority level' },
      { value: formStatusId, label: 'status' },
      { value: formDueAt, label: 'due date' },
    ]

    const missingFields = requiredFields
      .filter((field) => !field.value)
      .map((field) => field.label)

    if (missingFields.length > 0) {
      let message = ''

      if (missingFields.length === 1) {
        message = `Please select a ${missingFields[0]}.`
      } else if (missingFields.length === 2) {
        message = `Please select a ${missingFields[0]} and ${missingFields[1]}.`
      } else {
        const lastField = missingFields.pop()

        message = `Please select a ${missingFields.join(
          ', '
        )}, and ${lastField}.`
      }

      alert(message)
      setLoading(false)
      return
    }

    const hasProvider = !!formAssignedToProviderId
    const hasProviderType = !!formProviderTypeId

    // They can only be equivalent if both are empty
    if (hasProvider === hasProviderType) {
      alert('Please select either a provider or a provider type.')
      setLoading(false)
      return
    }

    if (
      formPatientId &&
      formTaskTypeId &&
      formPriorityLevelId &&
      formStatusId &&
      formDueAt
    ) {
      // Resolve the objects the table needs for rendering
      const patient = allActivePatients.find(
        (p) => p.patientId === formPatientId
      )

      const taskType = taskTypes.find((t) => t.id === formTaskTypeId)

      const assignedProviderType = formProviderTypeId
        ? providerTypes.find((p) => p.id === formProviderTypeId)?.name
        : null

      const assignedProviders = formAssignedToProviderId
        ? providers
            .filter((p) => p.providerId === formAssignedToProviderId)
            .map((p) => ({
              providerId: p.providerId,
              providerName: p.name,
            }))
        : null

      const priorityLevel = priorityLevels.find(
        (p) => p.id === formPriorityLevelId
      )

      const status = statuses.find((s) => s.id === formStatusId)

      const barrier = formBarrierId
        ? barriers.find((b) => b.id === formBarrierId) ?? null
        : null

      if (!taskType || !priorityLevel || !status) {
        alert('TaskType or PriorityLevel or Status not found.')
        setLoading(false)
        return
      }

      // Negative temp id — guaranteed not to collide with real server ids
      const tempId = -Date.now()

      const optimisticTask: ProviderTaskResponse = {
        id: tempId,
        patientId: formPatientId,
        patientName: patient?.preferredName ?? '',
        providerTaskType: taskType,

        assignedProviderType,
        assignedProviders,

        priorityLevel,
        status,
        barrier,
        assignedAt: dayjs().toISOString(),
        dueAt: formDueAt.toISOString(),
        dueDate: formDueAt.format('YYYY-MM-DD'),
        createdAt: dayjs().toISOString(),
        providerTaskComments: [],
      } as ProviderTaskResponse

      // Show it immediately, close modal right away
      setOptimisticTasks((prev) => [optimisticTask, ...prev])
      setShowCreateModal(false)

      try {
        const createdTaskId = await onCreateTask({
          patientId: formPatientId,
          providerTaskTypeId: formTaskTypeId,
          providerTypeId: formProviderTypeId,
          assignedToProviderId: formAssignedToProviderId,
          providerTaskPriorityLevelId: formPriorityLevelId,
          providerTaskStatusId: formStatusId,
          providerTaskBarrierId: formBarrierId,
          assignedAt: formAssignedAt?.toISOString() ?? dayjs().toISOString(),
          dueAt: formDueAt.toISOString(),
        })

        if (!createdTaskId) {
          // Backend failed — rollback
          setOptimisticTasks((prev) => prev.filter((t) => t.id !== tempId))
          setShowCreateModal(true)
          alert('Failed to create task. Please try again.')
          setLoading(false)
          return
        }

        // Swap the temp task out for the real one (real id, etc.)
        setOptimisticTasks((prev) =>
          prev.map((task) =>
            task.id === tempId ? { ...task, id: createdTaskId } : task
          )
        )
        setShowCreateModal(false)
      } catch (error) {
        console.log('onCreateTask-error', error)
        // Rollback — remove the optimistic local entry
        setOptimisticTasks((prev) => prev.filter((t) => t.id !== tempId))
        setShowCreateModal(true) // reopen so user can retry without losing form state
        alert('Failed to create task. Please try again.')
      } finally {
        setLoading(false)
      }
    }
    setLoading(false)
  }

  return (
    <Dialog
      open={showCreateModal}
      onClose={() => setShowCreateModal(false)}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle>Create New Task</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}
      >
        <Autocomplete
          size='small'
          fullWidth
          options={allActivePatients}
          getOptionLabel={(option) =>
            `${option.preferredName} (MRN: ${option.mrn})`
          }
          onChange={(_, value) => setFormPatientId(value?.patientId ?? 0)}
          renderOption={(props, option) => (
            <li {...props} key={option.patientId}>
              {option.preferredName}
            </li>
          )}
          renderInput={(params) => <TextField {...params} label='Patient' />}
          sx={{ mt: 1 }}
        />

        <Autocomplete
          size='small'
          fullWidth
          options={taskTypes}
          getOptionLabel={(option) => `${option.type}`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => setFormTaskTypeId(value?.id as number)}
          renderOption={(props, option) => (
            <li {...props} key={`task-type-${option.id}`}>
              {option.type}
            </li>
          )}
          renderInput={(params) => <TextField {...params} label='Task Type' />}
        />

        <Autocomplete
          size='small'
          fullWidth
          options={providerTypes}
          disabled={!!formAssignedToProviderId}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => {
            setFormProviderTypeId(value?.id ?? null)

            // Clear specific provider when selecting a provider type
            if (value) {
              setFormAssignedToProviderId(null)
            }
          }}
          renderOption={(props, option) => (
            <li {...props} key={`provider-type-${option.id}`}>
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                formAssignedToProviderId
                  ? 'Unable to select both provider type and provider'
                  : 'Provider Type (Optional)'
              }
            />
          )}
        />

        <Autocomplete
          size='small'
          fullWidth
          options={providers}
          disabled={!!formProviderTypeId}
          getOptionLabel={(option) => `${option.name}`}
          isOptionEqualToValue={(option, value) =>
            option.providerId === value.providerId
          }
          onChange={(_, value) => {
            setFormAssignedToProviderId(value?.providerId ?? null)

            // just in case, clear provider type when selecting a specific provider
            if (value) {
              setFormProviderTypeId(null)
            }
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.providerId}>
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                formProviderTypeId
                  ? 'Unable to select both provider type and provider'
                  : 'Assign to Provider'
              }
            />
          )}
        />

        <FormControl size='small' fullWidth>
          <InputLabel>Priority Level</InputLabel>
          <Select
            value={formPriorityLevelId}
            label='Priority Level'
            onChange={(e) => setFormPriorityLevelId(e.target.value as number)}
          >
            {priorityLevels.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.level}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size='small' fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={formStatusId}
            label='Status'
            onChange={(e) => setFormStatusId(e.target.value as number)}
          >
            {statuses.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size='small' fullWidth>
          <InputLabel>Barrier (Optional)</InputLabel>
          <Select
            value={formBarrierId ?? ''}
            label='Barrier (Optional)'
            onChange={(e) =>
              setFormBarrierId(
                e.target.value ? (e.target.value as number) : null
              )
            }
          >
            <MenuItem value=''>None</MenuItem>
            {barriers.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.barrier}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <DatePicker
          label='Due Date'
          value={formDueAt}
          onChange={(date) => setFormDueAt(date)}
          renderInput={(params: any) => (
            <TextField {...params} size='small' fullWidth />
          )}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setShowCreateModal(false)}>Cancel</Button>
        <Button variant='contained' onClick={handleCreateTask}>
          {loading ? (
            <CircularProgress size={20} color='secondary' />
          ) : (
            'Create'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
